import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import { collection, query, onSnapshot, orderBy, where, doc, updateDoc } from "firebase/firestore";
import {
    MessageSquare,
    Search,
    Filter,
    MoreHorizontal,
    AlertCircle,
    Clock,
    CheckCircle2,
    UserPlus,
    XCircle,
    ChevronRight,
    Tag,
    Building2,
    PriorityHigh,
    User
} from "lucide-react";

const StatusBadge = ({ status }) => {
    const styles = {
        open: "bg-red-50 text-red-700 border-red-100",
        in_progress: "bg-blue-50 text-blue-700 border-blue-100",
        waiting_client: "bg-amber-50 text-amber-700 border-amber-100",
        resolved: "bg-emerald-50 text-emerald-700 border-emerald-100",
        closed: "bg-slate-50 text-slate-700 border-slate-100"
    };
    return (
        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${styles[status] || styles.open}`}>
            {status?.replace("_", " ")}
        </span>
    );
};

const PriorityBadge = ({ priority }) => {
    const styles = {
        urgent: { color: "text-rose-600", bg: "bg-rose-50", icon: AlertCircle },
        high: { color: "text-amber-600", bg: "bg-amber-50", icon: AlertCircle },
        normal: { color: "text-blue-600", bg: "bg-blue-50", icon: Clock },
        low: { color: "text-slate-500", bg: "bg-slate-50", icon: Clock }
    };
    const config = styles[priority] || styles.normal;
    const Icon = config.icon;

    return (
        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${config.bg} ${config.color} border border-current/10 w-fit`}>
            <Icon size={12} strokeWidth={3} />
            <span className="text-[10px] font-bold uppercase tracking-tight">{priority}</span>
        </div>
    );
};

import TicketDetail from "./TicketDetail";

export default function SupportTicketsPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedTicketId, setSelectedTicketId] = useState(null);

    // Parse incoming ?ticketId= query param
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const urlTicketId = searchParams.get("ticketId");
        if (urlTicketId) {
            setSelectedTicketId(urlTicketId);
        }
    }, [location.search]);

    useEffect(() => {
        const q = query(collection(db, "tickets"));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const ticketData = [];
            snapshot.forEach(doc => ticketData.push({ id: doc.id, ...doc.data() }));

            ticketData.sort((a, b) => {
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
                return dateB - dateA;
            });

            setTickets(ticketData);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const formatTime = (ts) => {
        if (!ts) return "---";
        const date = ts.toDate ? ts.toDate() : new Date(ts);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const filteredTickets = tickets.filter(t => {
        const matchesSearch = t.ticketNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.companyId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.userId?.toLowerCase().includes(searchQuery.toLowerCase());

        let matchesFilter = true;
        if (filterStatus === "urgent") matchesFilter = t.priority === "urgent";
        else if (filterStatus === "waiting_client") matchesFilter = t.status === "waiting_client";
        else if (filterStatus === "in_progress") matchesFilter = t.status === "in_progress";
        else if (filterStatus === "open") matchesFilter = t.status === "open";
        else if (filterStatus === "resolved") matchesFilter = t.status === "resolved" || t.status === "closed";

        return matchesSearch && matchesFilter;
    });

    const stats = {
        total: tickets.length,
        open: tickets.filter(t => t.status === 'open').length,
        pending: tickets.filter(t => t.status === 'in_progress' || t.status === 'waiting_client').length,
        resolved: tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Header Area */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-navy-900 font-serif">Support Tickets</h1>
                    <p className="text-slate-500 mt-1">Manage global client inquiries and critical infrastructure requests.</p>
                </div>
                <div className="flex bg-white rounded-2xl border border-slate-200 p-1.5 shadow-sm overflow-x-auto max-w-full">
                    {[
                        { id: "all", label: "All" },
                        { id: "open", label: "Open" },
                        { id: "urgent", label: "Urgent" },
                        { id: "waiting_client", label: "Awaiting Reply" },
                        { id: "in_progress", label: "In Progress" },
                        { id: "resolved", label: "Resolved" }
                    ].map(s => (
                        <button
                            key={s.id}
                            onClick={() => setFilterStatus(s.id)}
                            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${filterStatus === s.id
                                ? "bg-navy-900 text-white shadow-lg"
                                : "text-slate-500 hover:text-navy-900 hover:bg-slate-50"}`}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[
                    { label: "Total Tickets", value: stats.total, icon: Tag, color: "text-blue-600", bg: "bg-blue-50" },
                    { label: "Active Open", value: stats.open, icon: AlertCircle, color: "text-red-600", bg: "bg-red-50" },
                    { label: "In Progress", value: stats.pending, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
                    { label: "Resolved", value: stats.resolved, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
                ].map((stat, i) => (
                    <div key={i} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${stat.bg} ${stat.color}`}>
                            <stat.icon size={20} strokeWidth={2.5} />
                        </div>
                        <div>
                            <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider">{stat.label}</p>
                            <p className="text-2xl font-bold text-navy-900">{stat.value}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Table Area */}
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="relative w-full md:w-96">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                        <input
                            type="text"
                            placeholder="Search ticket #, subject, or company..."
                            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-navy-900/10 outline-none transition-all"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <button className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-navy-900 hover:bg-slate-50 transition-all">
                        <Filter size={16} /> Advanced Filters
                    </button>
                </div>

                <div className="overflow-y-auto max-h-[600px] custom-scrollbar bg-slate-50/20">
                    {/* Desktop Table */}
                    <table className="w-full text-left border-collapse hidden md:table">
                        <thead className="sticky top-0 bg-white z-10">
                            <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100">
                                <th className="px-6 py-4">Ticket Details</th>
                                <th className="px-6 py-4">Tenant / User</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Priority</th>
                                <th className="px-6 py-4">Created At</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-slate-500 font-medium">
                                        <div className="flex flex-col items-center gap-3">
                                            <div className="w-6 h-6 border-3 border-navy-900/10 border-t-navy-900 rounded-full animate-spin"></div>
                                            Loading tickets...
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredTickets.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-slate-500 font-medium">
                                        No tickets found matching your criteria.
                                    </td>
                                </tr>
                            ) : (
                                filteredTickets.map((ticket) => (
                                    <tr
                                        key={ticket.id}
                                        onClick={() => setSelectedTicketId(ticket.id)}
                                        className="hover:bg-slate-50/80 transition-all cursor-pointer group"
                                    >
                                        <td className="px-6 py-5">
                                            <div className="flex flex-col">
                                                <span className="text-xs font-bold text-blue-600 uppercase tracking-tighter mb-1 font-mono">
                                                    {ticket.ticketNumber || 'NO-REF'}
                                                </span>
                                                <span className="text-sm font-bold text-navy-900 line-clamp-1 group-hover:text-blue-700 transition-colors">
                                                    {ticket.subject || 'No Subject'}
                                                </span>
                                                <span className="text-[10px] text-slate-400 font-medium mt-1">
                                                    {ticket.category || 'Support Request'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-2 text-xs font-bold text-navy-900">
                                                    <Building2 size={12} className="text-slate-400" />
                                                    {ticket.companyId || 'N/A'}
                                                </div>
                                                <div className="flex items-center gap-2 text-[10px] font-medium text-slate-500">
                                                    <User size={12} className="text-slate-400" />
                                                    {ticket.userId || 'System'}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <StatusBadge status={ticket.status} />
                                        </td>
                                        <td className="px-6 py-5">
                                            <PriorityBadge priority={ticket.priority} />
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex flex-col">
                                                <span className="text-xs font-medium text-navy-900">
                                                    {formatTime(ticket.createdAt)}
                                                </span>
                                                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter mt-0.5">
                                                    Standard SLA
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button className="p-2 text-slate-400 hover:text-navy-900 hover:bg-slate-100 rounded-lg transition-all flex items-center gap-1" title="View Ticket">
                                                    <span className="text-xs font-bold uppercase hidden md:inline-block">View</span>
                                                    <ChevronRight size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {/* Mobile Cards */}
                    <div className="block md:hidden p-4 space-y-4">
                        {loading ? (
                            <div className="py-12 text-center text-slate-500 font-medium">
                                <div className="flex flex-col items-center gap-3">
                                    <div className="w-6 h-6 border-3 border-navy-900/10 border-t-navy-900 rounded-full animate-spin"></div>
                                    Loading tickets...
                                </div>
                            </div>
                        ) : filteredTickets.length === 0 ? (
                            <div className="py-12 text-center text-slate-500 font-medium">
                                No tickets found matching your criteria.
                            </div>
                        ) : (
                            filteredTickets.map((ticket) => (
                                <div
                                    key={ticket.id}
                                    onClick={() => setSelectedTicketId(ticket.id)}
                                    className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col gap-3 relative cursor-pointer active:scale-[0.98] transition-all"
                                >
                                    <div className="flex justify-between items-start">
                                        <div className="flex flex-col">
                                            <span className="text-xs font-bold text-blue-600 uppercase tracking-tighter mb-0.5 font-mono">
                                                {ticket.ticketNumber || 'NO-REF'}
                                            </span>
                                            <span className="text-sm font-bold text-navy-900 line-clamp-2">
                                                {ticket.subject || 'No Subject'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex gap-2">
                                        <StatusBadge status={ticket.status} />
                                        <PriorityBadge priority={ticket.priority} />
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 mt-2 pt-3 border-t border-slate-50 text-xs">
                                        <div>
                                            <span className="text-slate-400 font-medium block">Tenant</span>
                                            <span className="font-bold text-navy-900 truncate flex items-center gap-1"><Building2 size={12} />{ticket.companyId || 'N/A'}</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-400 font-medium block">Created</span>
                                            <span className="font-medium text-slate-600">{formatTime(ticket.createdAt)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Ticket Detail Sidebar */}
            {selectedTicketId && (
                <TicketDetail
                    ticketId={selectedTicketId}
                    onClose={() => {
                        setSelectedTicketId(null);
                        // Clear the query parameter without refreshing
                        if (location.search.includes('ticketId')) {
                            navigate('/super-admin/tickets', { replace: true });
                        }
                    }}
                />
            )}
        </div>
    );
}
